/**
 * First-entry coach mark (design v3, variant B): a card anchored to the map
 * bar's ★ segment on special-rules maps, over a light scrim with a cutout and
 * a pulse round the segment. "Got it" collapses it into the button (a short
 * tip names where the rules live); "Show me the rules" opens the ★ overlay;
 * tapping the segment through its cutout does the same. Team games get no
 * coach mark of their own (maintainer, 2026-11-03). With reduced motion
 * there is no fade, pulse or collapse: the card appears and disappears.
 *
 * Positions come from measureInWindow on the segments, converted into this
 * component's own frame (it fills the game screen).
 */
import React from 'react'
import { Animated, Easing, Platform, Pressable, Text, View, useWindowDimensions } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import Svg, { Defs, Mask, Rect } from 'react-native-svg'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import type { FirstEntryKind } from '../../lib/firstEntryNotice'
import { overlayPalette } from '../../lib/rulesOverlayTheme'
import { MAP_BAR_ICONS } from '../map/MapControlsBar'

type Rect4 = { x: number; y: number; width: number; height: number }
type Measurable = { measureInWindow: (cb: (x: number, y: number, w: number, h: number) => void) => void }

export const COACH_COPY = {
  map: { title: 'Special rules map', primary: 'Show me the rules', tip: 'Map rules live here · tap any time' },
} as const

const measure = (ref: React.RefObject<Measurable | null> | undefined) => new Promise<Rect4 | null>((resolve) => {
  const node = ref?.current
  if (!node) return resolve(null)
  node.measureInWindow((x, y, width, height) => resolve(width > 0 && height > 0 ? { x, y, width, height } : null))
})

export function FirstEntryCoachMark({
  kind,
  theme,
  rulesAnchor,
  onGotIt,
  onShow,
  onDone,
}: {
  kind: FirstEntryKind
  theme: string
  rulesAnchor?: React.RefObject<Measurable | null>
  /** Got it (button, a tap on the scrim, Esc): mark seen. */
  onGotIt: () => void
  /** Show me the rules, or a tap on the ★ segment: mark seen and open the ★ overlay. */
  onShow: (overlay: 'map') => void
  /** The collapse tip has finished: unmount. */
  onDone: () => void
}) {
  const palette = overlayPalette(theme)
  const reducedMotion = useReducedMotion()
  const { width: winW, height: winH } = useWindowDimensions()
  const rootRef = React.useRef<View>(null)
  const [frame, setFrame] = React.useState<{ origin: { x: number; y: number }; size: { w: number; h: number }; rules: Rect4 | null } | null>(null)
  const [phase, setPhase] = React.useState<'point' | 'collapse' | 'tip'>('point')
  const [cardH, setCardH] = React.useState(0)
  const collapse = React.useRef(new Animated.Value(0)).current
  const opacity = React.useRef(new Animated.Value(reducedMotion ? 1 : 0)).current
  const pulse = React.useRef(new Animated.Value(0)).current

  // Measure the segments (window coordinates) and this overlay's own origin.
  React.useEffect(() => {
    let active = true
    let timer: ReturnType<typeof setTimeout> | undefined
    // The bar may still be laying out (or the board revealing): retry a few times.
    const run = async (attempt: number) => {
      const root = await measure(rootRef as React.RefObject<Measurable | null>)
      const rules = await measure(rulesAnchor)
      if (!active) return
      if (!root || !rules) {
        if (attempt < 20) timer = setTimeout(() => void run(attempt + 1), 300)
        return
      }
      const local = (r: Rect4 | null) => (r ? { ...r, x: r.x - root.x, y: r.y - root.y } : null)
      setFrame({ origin: { x: root.x, y: root.y }, size: { w: root.width, h: root.height }, rules: local(rules) })
    }
    // Let the board settle first, so the coach mark fades in over the live game.
    timer = setTimeout(() => void run(0), 600)
    return () => { active = false; if (timer) clearTimeout(timer) }
  }, [kind, rulesAnchor, winW, winH])

  React.useEffect(() => {
    if (!frame || reducedMotion) return
    Animated.timing(opacity, { toValue: 1, duration: 350, easing: Easing.out(Easing.quad), useNativeDriver: true }).start()
    const loop = Animated.loop(Animated.timing(pulse, { toValue: 1, duration: 1400, easing: Easing.out(Easing.quad), useNativeDriver: true }))
    loop.start()
    return () => loop.stop()
  }, [frame, reducedMotion, opacity, pulse])

  const gotIt = React.useCallback(() => {
    onGotIt()
    if (reducedMotion) { onDone(); return }
    // The card shrinks into the button, then a short tip names it.
    setPhase('collapse')
    Animated.timing(collapse, { toValue: 1, duration: 420, easing: Easing.in(Easing.quad), useNativeDriver: true }).start(() => setPhase('tip'))
  }, [onGotIt, onDone, reducedMotion, collapse])
  React.useEffect(() => {
    if (phase !== 'tip') return
    const t = setTimeout(onDone, 2800)
    return () => clearTimeout(t)
  }, [phase, onDone])
  // Esc = Got it (web).
  React.useEffect(() => {
    if (phase !== 'point' || Platform.OS !== 'web' || typeof window === 'undefined') return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') gotIt() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, gotIt])

  const targets = frame?.rules ? [frame.rules] : []
  const ready = !!frame && targets.length > 0
  const union = ready ? {
    x: Math.min(...targets.map((t) => t.x)), y: Math.min(...targets.map((t) => t.y)),
    r: Math.max(...targets.map((t) => t.x + t.width)), b: Math.max(...targets.map((t) => t.y + t.height)),
  } : null
  const W = frame?.size.w ?? winW
  const H = frame?.size.h ?? winH
  const cx = union ? (union.x + union.r) / 2 : 0
  const phone = W < 700
  const cardW = Math.min(phone ? 270 : 320, W - 20)
  const cardLeft = Math.round(Math.max(10, Math.min(cx - cardW / 2, W - cardW - 10)))

  const inside = (r: Rect4, x: number, y: number) => x >= r.x - 4 && x <= r.x + r.width + 4 && y >= r.y - 13 && y <= r.y + r.height + 13
  const onScrimPress = (event: { nativeEvent: { pageX: number; pageY: number } }) => {
    if (!frame) return
    const x = event.nativeEvent.pageX - frame.origin.x
    const y = event.nativeEvent.pageY - frame.origin.y
    if (frame.rules && inside(frame.rules, x, y)) return onShow('map')
    gotIt()
  }

  const ring = (t: Rect4, k: string) => (
    <View key={k} pointerEvents="none" style={{ position: 'absolute', left: t.x - 4, top: t.y - 4, width: t.width + 8, height: t.height + 8 }}>
      <View style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, borderWidth: 2.5, borderColor: '#e39b3c', borderRadius: 9 }} />
      {reducedMotion ? null : (
        <Animated.View
          style={{
            position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, borderWidth: 3, borderColor: '#e39b3c', borderRadius: 11,
            opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.9, 0] }),
            transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.25] }) }],
          }}
        />
      )}
    </View>
  )

  const icon = (size: number) => (
    <MaterialCommunityIcons name={MAP_BAR_ICONS.rules} size={size} color={palette.amber} />
  )
  const key = (
    <View style={{ borderWidth: 1, borderColor: palette.olive, borderRadius: 5, paddingHorizontal: 4, paddingVertical: 1, backgroundColor: palette.paper, marginHorizontal: 2, transform: [{ translateY: 3 }] }}>
      {icon(15)}
    </View>
  )
  const badge = (
    <View style={{ width: 30, height: 30, borderRadius: 8, borderWidth: 1.5, borderColor: palette.amber, backgroundColor: palette.paperUp, alignItems: 'center', justifyContent: 'center' }}>
      {icon(18)}
    </View>
  )
  const button = (label: string, primary: boolean, onPress: () => void, testID: string) => (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={{
        height: 36, paddingHorizontal: 14, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
        backgroundColor: primary ? palette.blood : palette.paper, borderWidth: primary ? 0 : 1, borderColor: palette.olive,
      }}
    >
      <Text className="font-stencil" style={{ fontSize: 11, letterSpacing: 0.3, color: primary ? '#fff' : palette.ink }}>{label}</Text>
    </Pressable>
  )
  const body = (text: string) => <Text className="font-typed" style={{ fontSize: 15, lineHeight: 20, color: palette.ink }}>{text}</Text>

  const card = (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {badge}
        <Text accessibilityRole="header" className="font-stencil" style={{ flex: 1, fontSize: 14, lineHeight: 17, color: palette.ink }}>{COACH_COPY[kind].title}</Text>
      </View>
      <View style={{ marginTop: 8, marginBottom: 12, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
        {body('This board plays differently. Tap ')}{key}{body(' to see how.')}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
        {button('Got it', false, gotIt, 'coach-got-it')}
        {button(COACH_COPY[kind].primary, true, () => onShow('map'), 'coach-show-map')}
      </View>
    </>
  )

  return (
    <View ref={rootRef} pointerEvents={phase === 'point' ? 'box-none' : 'none'} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, zIndex: 60 }} testID="first-entry-coach">
      {ready && union && phase !== 'tip' ? (
        <Animated.View style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0, opacity: phase === 'collapse' ? collapse.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }) : opacity }}>
          <Pressable accessible={false} onPress={onScrimPress} style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
            <Svg width={W} height={H} pointerEvents="none">
              <Defs>
                <Mask id="coach-scrim" maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={H}>
                  <Rect x={0} y={0} width={W} height={H} fill="#fff" />
                  {targets.map((t, i) => <Rect key={i} x={t.x - 4} y={t.y - 4} width={t.width + 8} height={t.height + 8} rx={8} fill="#000" />)}
                </Mask>
              </Defs>
              <Rect x={0} y={0} width={W} height={H} fill={palette.coachScrim} mask="url(#coach-scrim)" />
            </Svg>
          </Pressable>
          {targets.map((t, i) => ring(t, `r${i}`))}
          <Animated.View
            testID="first-entry-coach-card"
            accessibilityViewIsModal
            accessibilityLabel={COACH_COPY[kind].title}
            onLayout={(event) => setCardH(event.nativeEvent.layout.height)}
            style={{
              transform: [
                { translateX: collapse.interpolate({ inputRange: [0, 1], outputRange: [0, cx - (cardLeft + cardW / 2)] }) },
                { translateY: collapse.interpolate({ inputRange: [0, 1], outputRange: [0, cardH / 2 + 16] }) },
                { scale: collapse.interpolate({ inputRange: [0, 1], outputRange: [1, 0.08] }) },
              ],
              position: 'absolute', left: cardLeft, width: cardW, bottom: H - union.y + 16, borderWidth: 2, borderColor: palette.olive, borderRadius: 16,
              backgroundColor: palette.paper, paddingHorizontal: 16, paddingVertical: 14,
              shadowColor: '#000', shadowOpacity: 0.4, shadowRadius: 16, shadowOffset: { width: 0, height: 12 }, elevation: 12,
            }}
          >
            {card}
            {/* The caret, pointing down at the segment(s). */}
            <View
              pointerEvents="none"
              style={{
                position: 'absolute', left: cx - cardLeft - 7, bottom: -9, width: 14, height: 14, backgroundColor: palette.paper,
                borderRightWidth: 2, borderBottomWidth: 2, borderColor: palette.olive, transform: [{ rotate: '45deg' }],
              }}
            />
          </Animated.View>
        </Animated.View>
      ) : null}
      {ready && union && phase === 'tip' ? (
        <>
          {targets.map((t, i) => ring(t, `t${i}`))}
          <View
            pointerEvents="none"
            style={{
              position: 'absolute', left: Math.max(6, Math.min(cx - 125, W - 256)), width: 250, top: union.y - (phone ? 52 : 44),
              backgroundColor: palette.ink, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6,
            }}
          >
            <Text className="font-stencil" style={{ fontSize: 10, lineHeight: 14, color: palette.paper, textAlign: 'center' }}>{COACH_COPY[kind].tip}</Text>
          </View>
        </>
      ) : null}
    </View>
  )
}
